import {
  api,
  listAgents,
  fetchAgent,
  createAgentApi,
  patchAgent,
  deleteAgentApi,
  fetchKnowledgeBase,
  deleteKnowledgeBaseEntryApi,
  fetchMasterMemory,
  deleteMasterMemoryEntryApi,
  clearMasterMemoryApi,
  fetchThreadMemoryMd,
  fetchWaitingConversations,
  deleteWaitingConversationApi,
  clearWaitingConversationsApi,
  fetchListenerMemory,
  clearListenerMemoryApi,
  fetchEscalationLogs,
  deleteEscalationLogApi,
  fetchAgentFiles,
  fetchAgentFileText,
  uploadAgentFiles,
  deleteAgentFileApi,
  deleteAgentFolderApi,
  downloadFile,
  uploadFolder,
} from "./api";

export async function getAgentFiles(agentId) {
  return fetchAgentFiles(agentId);
}

export async function getAgentFileText(agentId, fileId) {
  return fetchAgentFileText(agentId, fileId);
}

export async function postAgentFiles(agentId, files, opts) {
  return uploadAgentFiles(agentId, files, opts);
}

export async function removeAgentFile(agentId, fileId) {
  return deleteAgentFileApi(agentId, fileId);
}

export async function removeAgentFolder(agentId, folderName) {
  return deleteAgentFolderApi(agentId, folderName);
}

export async function createAgent(userId, agentData) {
  const id = await createAgentApi(userId, agentData);
  return id;
}

export async function getAgents(userId) {
  return listAgents(userId);
}

export async function getAgent(agentId) {
  return fetchAgent(agentId);
}

export { fetchAgent };

export async function updateAgent(agentId, data) {
  return patchAgent(agentId, { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteAgent(agentId) {
  return deleteAgentApi(agentId);
}

export async function getKnowledgeBase(agentId) {
  return fetchKnowledgeBase(agentId);
}

export async function deleteKnowledgeBaseEntry(agentId, entryId) {
  return deleteKnowledgeBaseEntryApi(agentId, entryId);
}

export async function getMasterMemory(agentId, maxEntries = 200) {
  return fetchMasterMemory(agentId, maxEntries);
}

export async function deleteMasterMemoryEntry(agentId, entryId) {
  return deleteMasterMemoryEntryApi(agentId, entryId);
}

export async function clearMasterMemory(agentId) {
  return clearMasterMemoryApi(agentId);
}

export async function getThreadMemoryMd(agentId, threadId) {
  return fetchThreadMemoryMd(agentId, threadId);
}

export async function getWaitingConversations(agentId) {
  return fetchWaitingConversations(agentId);
}

export async function deleteWaitingConversation(agentId, entryId) {
  return deleteWaitingConversationApi(agentId, entryId);
}

export async function clearWaitingConversations(agentId) {
  return clearWaitingConversationsApi(agentId);
}

export async function getListenerMemory(_agentId, listenerId, maxEntries = 50) {
  return fetchListenerMemory(listenerId, maxEntries);
}

export async function clearListenerMemory(_agentId, listenerId) {
  return clearListenerMemoryApi(listenerId);
}

export async function getEscalationLogs(agentId) {
  return fetchEscalationLogs(agentId);
}

export async function deleteEscalationLog(agentId, logId) {
  return deleteEscalationLogApi(agentId, logId);
}

export async function listAgentSkills(agentId) {
  const data = await api.get(`/agents/${encodeURIComponent(agentId)}/skills`);
  return Array.isArray(data) ? data : (data?.skills || []);
}

export async function getAgentSkill(agentId, skillId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}`);
}

export async function readAgentSkillFile(agentId, skillId, path) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/file`, { path });
}

export async function writeAgentSkillFile(agentId, skillId, path, content) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/file`, { path, content });
}

export async function deleteAgentSkillFile(agentId, skillId, path) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/file`, { path });
}

export async function downloadAgentSkillZip(agentId, skillId) {
  return downloadFile(
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/download`,
    `${skillId}.zip`,
  );
}

export async function uploadAgentSkillFolder(agentId, skillId, files, opts) {
  return uploadFolder(
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/upload`,
    files,
    opts,
  );
}

export async function listAgentListenerFiles(agentId, listenerId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/files`);
}

export async function readAgentListenerFile(agentId, listenerId, path) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/file`, { path });
}

export async function writeAgentListenerFile(agentId, listenerId, path, content) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/file`, { path, content });
}

export async function deleteAgentListenerFile(agentId, listenerId, path) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/file`, { path });
}

export async function downloadAgentListenerZip(agentId, listenerId) {
  return downloadFile(
    `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/download`,
    `${listenerId}.zip`,
  );
}

export async function uploadAgentListenerFolder(agentId, listenerId, files, opts) {
  return uploadFolder(
    `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/upload`,
    files,
    opts,
  );
}

export async function runAgentSkillCommand(agentId, skillId, command, { timeout, shell } = {}) {
  const body = { command };
  if (timeout) body.timeout = timeout;
  if (shell) body.shell = true;
  return api.post(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/run`, body);
}

export async function listAgentListeners(agentId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/listeners`);
}

export async function updateListenerSpec(agentId, listenerId, updates) {
  return api.put(
    `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/spec`,
    updates,
  );
}

export async function resumeListener(agentId, listenerId) {
  return api.post(
    `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/resume`,
    {},
  );
}

export async function getAgentSkillCredentials(agentId, skillId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/credentials`);
}

export async function putAgentSkillCredentials(agentId, skillId, body) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/credentials`, body);
}

export async function getAgentSkillCredentialSuggestions(agentId, skillId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/credential-suggestions`);
}

export async function copyAgentSkillCredentialsFrom(agentId, skillId, fromSlug, keys) {
  return api.post(
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/credentials/copy`,
    { from: fromSlug, keys }
  );
}

export async function enableAgentSkill(agentId, skillId, config) {
  return api.post(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/enable`, config ? { config } : {});
}

export async function disableAgentSkill(agentId, skillId) {
  return api.post(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/disable`, {});
}

export async function installAgentSkill(agentId, payload) {
  return api.post(`/agents/${encodeURIComponent(agentId)}/skills`, payload);
}

export async function installListenerFromCatalog(agentId, listenerId, itemId) {
  return api.post(
    `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/install-from-catalog`,
    { item_id: itemId },
  );
}

export async function uninstallAgentSkill(agentId, skillId) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}`);
}

export async function fetchCatalog() {
  return api.get(`/catalog`);
}

export async function suggestCatalog({ name, description }) {
  return api.post(`/catalog/suggest`, { name, description });
}

export async function testCatalogConnection({ itemId, kind, credentials, settings, agentId }) {
  return api.post(`/catalog/test-connection`, {
    item_id: itemId,
    kind,
    credentials,
    settings,
    agent_id: agentId,
  });
}

export async function setAgentSkillMode(agentId, skillId, mode) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/mode`, { mode });
}

export async function reloadService() {
  return api.post(`/service/reload`, {});
}

export async function fetchAgentSkillRuns(agentId, skillId, { limit = 100 } = {}) {
  const qs = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  return api.get(
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/runs${qs}`,
  );
}

export async function clearAgentSkillRuns(agentId, skillId) {
  return api.del(
    `/agents/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}/runs`,
  );
}

// ----- subagents -----------------------------------------------------------

export async function listAgentSubagents(agentId) {
  const data = await api.get(`/agents/${encodeURIComponent(agentId)}/subagents`);
  return Array.isArray(data) ? data : (data?.subagents || []);
}

export async function getAgentSubagent(agentId, subId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}`);
}

export async function installAgentSubagent(agentId, payload) {
  return api.post(`/agents/${encodeURIComponent(agentId)}/subagents`, payload);
}

export async function uninstallAgentSubagent(agentId, subId) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}`);
}

export async function readAgentSubagentFile(agentId, subId, path) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/file`, { path });
}

export async function writeAgentSubagentFile(agentId, subId, path, content) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/file`, { path, content });
}

export async function deleteAgentSubagentFile(agentId, subId, path) {
  return api.del(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/file`, { path });
}

export async function listAdapters() {
  const data = await api.get('/adapters');
  return Array.isArray(data) ? data : (data?.adapters || []);
}

export async function getAgentSubagentCredentials(agentId, subId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/credentials`);
}

export async function putAgentSubagentCredentials(agentId, subId, body) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/credentials`, body);
}

export async function getAgentSubagentParentMemory(agentId, subId) {
  return api.get(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/parent-memory`);
}

export async function setAgentSubagentRelayNumber(agentId, subId, number) {
  return api.put(`/agents/${encodeURIComponent(agentId)}/subagents/${encodeURIComponent(subId)}/relay-number`, { number });
}
